import type { Option, QuantityDiscountDTO, QuantityDiscountTierDTO } from 'ecosystem'

/**
 * One homogeneous run of units within a single cart line.
 *
 * A `THRESHOLD` allocation always produces exactly one block. A `PACK`
 * allocation produces one block per complete discounted block plus, when the
 * quantity does not divide evenly, a tail block at full price — which is the
 * whole reason a line cannot be described by a single unit price.
 */
export interface QuantityDiscountBlock {
  quantity: number
  /** Already discounted, in whatever unit `unitPrice` was passed in. */
  unitPrice: number
  /** `0` for a full-price block. */
  percentage: number
}

/**
 * Where a line's quantity sits against its ladder. Drives which teaser copy a
 * surface renders.
 */
export type QuantityDiscountState =
  /** No ladder at all — nothing to say. */
  | 'NONE'
  /** Below the first breakpoint. The offer is still an invitation. */
  | 'BELOW_FIRST'
  /** A tier is active, and a better one is still reachable. */
  | 'PROGRESS'
  /** At or above the top tier — nothing left to unlock. */
  | 'ACHIEVED'

export interface QuantityDiscountSummary {
  state: QuantityDiscountState
  mode: QuantityDiscountDTO['mode'] | null
  /** Ladder as stored, sorted ascending. Empty when there is no offer. */
  tiers: QuantityDiscountTierDTO[]
  /** The tier currently in play, or null below the first breakpoint. */
  currentTier: QuantityDiscountTierDTO | null
  /** The next tier up, or null once the top one is reached. */
  nextTier: QuantityDiscountTierDTO | null
  /** Units the shopper must still add to reach `nextTier`. `0` when there is none. */
  unitsToNext: number
  /** Units on the line that carry a discount. Below `quantity` on a `PACK` remainder. */
  discountedUnits: number
  /** Units on the line still at full price. */
  fullPriceUnits: number
  /** The ladder's best percentage, whether or not the shopper has reached it. */
  bestTier: QuantityDiscountTierDTO | null
}

/**
 * A ladder is usable only when it carries at least one tier. The backend also
 * uses an empty `tiers` list on the wire as "clear what you cached", so an empty
 * array reaching the FE means the same thing it means everywhere else: no offer.
 */
export const hasQuantityDiscount = (
  config: Option<QuantityDiscountDTO> | undefined
): config is QuantityDiscountDTO => Boolean(config?.tiers?.length)

/** Ascending by `minQuantity`. The backend sorts already; this survives a publisher that does not. */
const sortedAscending = (tiers: QuantityDiscountTierDTO[]): QuantityDiscountTierDTO[] =>
  [...tiers].sort((a, b) => a.minQuantity - b.minQuantity)

/**
 * Round half-up to 2 decimals, expressed in the caller's units.
 *
 * Checkout math runs in minor units (`createMinorUnit` is a bare `value * 100`
 * with no rounding of its own), where 2 decimals major is a whole minor unit —
 * so `minorUnits` picks the right precision rather than the caller rounding
 * afterwards and drifting.
 */
const roundUnitPrice = (value: number, minorUnits: boolean): number =>
  minorUnits ? Math.round(value) : Math.round(value * 100) / 100

export interface AllocateOptions {
  /** True when `unitPrice` is in minor units (the checkout path). Default false. */
  minorUnits?: boolean
}

/**
 * Split a cart line into priced blocks — the canonical platform algorithm.
 *
 * `unitPrice` is whatever price the surface already resolved for this buyer:
 * the public price, a campaign SALE, a B2B subscriber's negotiated price, or
 * any of those with a cart discount code already taken off. The ladder is a
 * percentage off *that*, never a reach back into the public chain.
 *
 * Tiers are read **descending** so the best applicable one wins. The discounted
 * unit price is rounded before it is multiplied by the block's unit count —
 * rounding the line total instead puts a storefront cart and the order Order
 * Management writes for the same basket a cent apart.
 *
 * @see CONNECT_INTEGRATION_GUIDE.md §6.3
 */
export const allocateQuantityDiscount = (
  quantity: number,
  unitPrice: number,
  config: Option<QuantityDiscountDTO> | undefined,
  { minorUnits = false }: AllocateOptions = {}
): QuantityDiscountBlock[] => {
  const fullPrice: QuantityDiscountBlock[] = [{ quantity, unitPrice, percentage: 0 }]

  if (quantity <= 0 || !hasQuantityDiscount(config)) {
    return fullPrice
  }

  const descending = sortedAscending(config.tiers).reverse()
  const discounted = (percentage: number) =>
    roundUnitPrice(unitPrice * (1 - percentage / 100), minorUnits)

  if (config.mode === 'THRESHOLD') {
    // One tier governs the whole line: the highest one the quantity reaches.
    const tier = descending.find((t) => quantity >= t.minQuantity)

    return tier
      ? [{ quantity, unitPrice: discounted(tier.percentage), percentage: tier.percentage }]
      : fullPrice
  }

  // PACK — fill greedily with the largest block that still fits, repeatedly.
  const blocks: QuantityDiscountBlock[] = []
  let remaining = quantity

  descending.forEach((tier) => {
    // A tier with a non-positive minQuantity would never consume any units and
    // would spin here forever. The backend validates minQuantity >= 1, but this
    // loop is the one place where trusting it would hang the browser.
    if (tier.minQuantity < 1) return

    while (remaining >= tier.minQuantity) {
      blocks.push({
        quantity: tier.minQuantity,
        unitPrice: discounted(tier.percentage),
        percentage: tier.percentage
      })
      remaining -= tier.minQuantity
    }
  })

  if (remaining > 0) {
    blocks.push({ quantity: remaining, unitPrice, percentage: 0 })
  }

  return blocks.length ? blocks : fullPrice
}

/** Line total across an allocation. */
export const sumQuantityDiscountBlocks = (blocks: QuantityDiscountBlock[]): number =>
  blocks.reduce((total, block) => total + block.quantity * block.unitPrice, 0)

/**
 * What the ladder takes off this line, in the units `unitPrice` was passed in.
 * Zero when no tier applies, so callers can add it unconditionally.
 */
export const quantityDiscountAmount = (
  quantity: number,
  unitPrice: number,
  config: Option<QuantityDiscountDTO> | undefined,
  options?: AllocateOptions
): number => {
  if (quantity <= 0 || !hasQuantityDiscount(config)) {
    return 0
  }

  const blocks = allocateQuantityDiscount(quantity, unitPrice, config, options)
  const discount = quantity * unitPrice - sumQuantityDiscountBlocks(blocks)

  // A 0% tier or a rounding artefact must never *raise* the line total.
  return discount > 0 ? discount : 0
}

/**
 * The line's effective per-unit price — its total spread across every unit.
 *
 * This is the blended figure the storefront order and Order Management need:
 * `OrderItem` carries a single `unitPrice`, so a `PACK` line of 25 (20 at −10%,
 * 5 at full price) has to arrive as one average. Payment providers do **not**
 * need it — each of the five carries the reduction as a discount amount on the
 * row or as a sibling discount line, so the shopper sees the split.
 */
export const blendedUnitPrice = (
  quantity: number,
  unitPrice: number,
  config: Option<QuantityDiscountDTO> | undefined,
  options?: AllocateOptions
): number => {
  if (quantity <= 0) return unitPrice

  const blocks = allocateQuantityDiscount(quantity, unitPrice, config, options)

  return sumQuantityDiscountBlocks(blocks) / quantity
}

/**
 * Score a quantity against a ladder for display purposes.
 *
 * Quantity `0` is legitimate and common — a product card or an untouched PDP
 * has no line yet, and still wants to advertise the first breakpoint.
 */
export const quantityDiscountSummary = (
  quantity: number,
  config: Option<QuantityDiscountDTO> | undefined
): QuantityDiscountSummary => {
  const empty: QuantityDiscountSummary = {
    state: 'NONE',
    mode: null,
    tiers: [],
    currentTier: null,
    nextTier: null,
    unitsToNext: 0,
    discountedUnits: 0,
    fullPriceUnits: Math.max(quantity, 0),
    bestTier: null
  }

  if (!hasQuantityDiscount(config)) {
    return empty
  }

  const tiers = sortedAscending(config.tiers)
  const bestTier = tiers[tiers.length - 1] ?? null

  // Blocks give the discounted/full split for free, and under PACK that split
  // is the whole message — "5 of your 25 are still full price".
  const blocks = allocateQuantityDiscount(quantity, 1, config)
  const discountedUnits = blocks
    .filter((block) => block.percentage > 0)
    .reduce((sum, block) => sum + block.quantity, 0)
  const fullPriceUnits = Math.max(quantity - discountedUnits, 0)

  // The best percentage actually in play, which under PACK is the best of the
  // blocks rather than "the highest tier the total reaches".
  const appliedPercentage = blocks.reduce((best, block) => Math.max(best, block.percentage), 0)
  const currentTier = appliedPercentage
    ? ([...tiers].reverse().find((tier) => tier.percentage === appliedPercentage) ?? null)
    : null

  if (config.mode === 'PACK') {
    // Under PACK the nudge is never "reach a bigger total" — it is "complete one
    // more block". The cheapest improvement is therefore the smallest tier the
    // leftover units do not yet fill, which is also the right answer when there
    // are no leftovers at all (a whole fresh block).
    const atCeiling = fullPriceUnits === 0 && appliedPercentage === (bestTier?.percentage ?? 0)
    const nextTier = atCeiling
      ? null
      : (tiers.find((tier) => tier.minQuantity > fullPriceUnits) ?? null)

    return {
      state: atCeiling ? 'ACHIEVED' : currentTier ? 'PROGRESS' : 'BELOW_FIRST',
      mode: config.mode,
      tiers,
      currentTier,
      nextTier,
      unitsToNext: nextTier ? nextTier.minQuantity - fullPriceUnits : 0,
      discountedUnits,
      fullPriceUnits,
      bestTier
    }
  }

  const nextTier = tiers.find((tier) => quantity < tier.minQuantity) ?? null

  return {
    state: currentTier ? (nextTier ? 'PROGRESS' : 'ACHIEVED') : 'BELOW_FIRST',
    mode: config.mode,
    tiers,
    currentTier,
    nextTier,
    unitsToNext: nextTier ? Math.max(nextTier.minQuantity - quantity, 0) : 0,
    discountedUnits,
    fullPriceUnits,
    bestTier
  }
}
